import { Container, ContainerGutter, Spinner, Text, TokenTextAppearance, TextTag } from '@volkswagen-onehub/components-core';
import React, { useContext, useEffect, useState } from 'react';
import { useCalculatorFinancial } from '../../../../store/calculator/financial';
import { CustomDetailsCTA } from '../../../../styles/components';
import { getLang, getTranslations } from '../../../../translations';
import { formatCurrency } from '../../../../utils/format';
import BuildPriceFieldset from '../../BuildPriceFieldset';
import Downpayment from '../options/Downpayment';
import FinanceTerms from '../options/FinanceTerms';
import IncludeTax from '../options/IncludeTax';
import WeeklyFrequency from '../options/WeeklyFreequency';
import { useFeatureServices } from '../../../../use-feature-services';
import { createConfiguration, createConstantData, createPartner, createProcess } from '../../../../utils/get-tracking-event-data';
import { ApplyForCredit } from './ApplyForCredit';
import appConfig from '../../../../app.config';
import { TermsDataType } from '../../../../types/finance';
import { VehicleDetailsContext } from '../../../../utils/details-context';

interface Credit {
  [key: string]: number | string;
}

export default () => {
    const { tracking } = useFeatureServices();
    const lang = getLang();
    const trans = getTranslations();
    const context = useContext(VehicleDetailsContext);
    const vehicle = context.vehicle;
    const dealer = context.dealer;
    const calculator = useCalculatorFinancial(state => state);
    const salesTax = calculator.financingRates?.provincialTax;
    const vehiclePrice = vehicle?.price;
    const rates = calculator.financingRates?.rates;

    const [applyingCredit, setApplyingCredit] = useState<boolean>(false);
    const [terms, setTerms] = useState <TermsDataType | null>(null);

    const onApplyCredit = async (e: React.SyntheticEvent<HTMLButtonElement>) => {
        const linkName = e.currentTarget.innerText;

        if(vehicle && dealer) {
            setApplyingCredit(true);
            const credit_msrp = vehicle.price;
            const credit_fees = Number(calculator.financingRates?.credit_fees[dealer.province]);

            const credit: Credit = {
                ft: 'Finance',
                lng: lang == 'fr' ? 'F' : 'E',
                mdln: vehicle?.model_name,
                mdly: vehicle?.year.toString(),
                tname: vehicle.trim[lang] as string,
                // dlr: dealer?.dealer_id.toString(),
                lrname: dealer?.name,
                lradd1: dealer?.address,
                dlrcity: dealer?.city,
                dlrstate: dealer?.province,
                dlrzip: dealer?.postal,
                dlrphone: dealer?.phone,
                vin: vehicle?.vin,
                typ: 'Used',
                cpo: '1',
                stk: vehicle?.stock_number,
                odo: vehicle?.mileage,
                country: 'ca',
                make: 'volkswagen',

                // calculator data
                dp: Number(calculator.downpayment),
                term: calculator.duration?.month as unknown as string,
            };

            const amf = Math.round(((credit_msrp + credit_fees) * (1 + Number(calculator.salesTax)) - Number(calculator.downpayment)) * 100) / 100;

            if (amf) { credit.amf = amf; }

            tracking.track('VWBasic_Button_Click', {
                ...createConstantData("VWBasic_Button_Click", vehicle.category),
                configuration: createConfiguration('VWBasic_Button_Click', vehicle, lang),
                partner: createPartner(dealer),
                process: createProcess("3"),
                link: {
                    url: appConfig.api.vwf,
                    name: linkName
                }
            })

            setTimeout(() => {
                ApplyForCredit({data : credit, onClose : () => setApplyingCredit(false)}).render("#apply-for-credit", "popup");
            }, 500);
        }
    }

    useEffect(() => {
        if(!salesTax || !vehiclePrice || !dealer) return;

        calculator.setSalesTax(salesTax[dealer?.province] as number);
        calculator.setVehiclePrice(vehiclePrice);

        calculator.setCalculatedValue();
    }, [salesTax, vehiclePrice, dealer]);

    useEffect(() => {
        if(rates == null || vehicle == null) return;

        rates.forEach(rate => {
            if(rate.type.details.en == appConfig.calculator.financeRate.name) {
                const vehicleName = vehicle.model_name.toLocaleLowerCase().replace(/[ .]/g, '');
                const vehicleRates = rate.type.models[vehicleName][0].rates;

                vehicleRates.forEach(vehicleRate => {
                    if(vehicleRate.year == vehicle.year) {
                        let termsData : TermsDataType = {
                            "24" : vehicleRate.months24,
                            "36" : vehicleRate.months36,
                            "48" : vehicleRate.months48,
                            "60" : vehicleRate.months60,
                            "72" : vehicleRate.months72,
                            "84" : vehicleRate.months84,
                        }

                        Object.keys(termsData).forEach(k => {
                            if(termsData[k] == '-') {
                                delete termsData[k];
                            }
                        });

                        const key = Object.keys(termsData)[Object.keys(termsData).length - 1];
                        calculator.setDuration({ month: key as unknown as number, rate: termsData[key as unknown as number] as unknown as number});
                        setTerms(termsData);
                    }
                });
            }
        });

    }, [rates, vehicle]);

    return (vehicle && dealer && terms) ?
        <BuildPriceFieldset legend={trans.labels.financial_calculator}>
            <Container
                wrap="always"
                gutter={ContainerGutter.static300}>
                <Container
                    gutter={ContainerGutter.static100}
                    wrap="always">
                    <Container
                        wrap="never"
                        verticalAlign="flex-start"
                        gutter={ContainerGutter.static150}>
                        {calculator.calculatedValue != null &&
                            <Text bold appearance={TokenTextAppearance.headline300}>{formatCurrency(calculator.calculatedValue as number, true, lang)}</Text>
                        }
                        <Text tag={TextTag.span} appearance={TokenTextAppearance.headline200}>{calculator.includeTax ? trans.details.calculator.payment_including : trans.details.calculator.payment_excluding}</Text>
                    </Container>

                    <IncludeTax taxHandler={calculator.setIncludeTax} checked={calculator.includeTax as boolean} />
                </Container>

                <FinanceTerms terms={terms} />

                <WeeklyFrequency frequencyHandler={calculator.setFrequency} />

                <Downpayment downPaymentHandler={calculator.setDownpayment}/>

                <Container
                    horizontalAlign="space-between"
                    verticalAlign="center"
                    wrap="auto"
                    gutter={ContainerGutter.static100}>
                    <Text appearance={TokenTextAppearance.copy200} bold>{calculator.frequency?.text} {trans.details.calculator.payment_card_foot}</Text>

                    {calculator.calculatedValue != null &&
                        <Text appearance={TokenTextAppearance.bigcopy200} bold>{formatCurrency(!isNaN(calculator.paymentAmount as number,) ? calculator.paymentAmount as number : 0, true, lang)}<Text bold={false} sup>1</Text></Text>
                    }
                </Container>

                {!applyingCredit ?
                    <CustomDetailsCTA
                        tag="button"
                        emphasis="primary"
                        onClick={(e: React.SyntheticEvent<HTMLButtonElement>) => onApplyCredit(e)}
                        ariaLabel={trans.details.calculator.payment_cta_credit}
                        // size='small'
                        paddingX="2rem">
                        {trans.details.calculator.payment_cta_credit}
                    </CustomDetailsCTA> :
                    <>
                        <Spinner />
                        <div id="apply-for-credit" key="vwf" />
                    </>
                }

                <Text tag={TextTag.p} appearance={TokenTextAppearance.copy100}>
                    <Text tag={TextTag.span} sup>1 </Text> {trans.details.calculator.payment_legal}
                </Text>

            </Container>
        </BuildPriceFieldset> : null;
}
